import type { FC, ReactNode } from 'react'
import type { Course } from '@/types'

import Link from 'next/link'
import { ArrowRight, Clock } from 'lucide-react'
import { courseHref } from '@/lib/routes'
import { CmsImage } from '@/components/generals/cms-image'
import { LevelBadge } from '@/components/courses/level-badge'

interface CourseCardProps {
    course: Course
}

export const CourseCard: FC<CourseCardProps> = ({ course }): ReactNode => {
    const href = courseHref(course.slug)

    return (
        <article className='group flex h-full flex-col overflow-hidden rounded-3xl border bg-card shadow-sm shadow-foreground/5 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-foreground/10'>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='relative block aspect-[16/10] overflow-hidden bg-secondary'
            >
                <CmsImage
                    src={course.image}
                    alt={course.image_alt}
                    sizes='(min-width: 1280px) 400px, (min-width: 640px) 50vw, 100vw'
                    className='transition duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                />
            </Link>

            <div className='flex flex-1 flex-col p-5 sm:p-6'>
                <p className='font-mono text-[11px] font-medium tracking-wider text-primary uppercase'>
                    <span data-mdlfy-cms={`courses:${course.id}:category`}>{course.category}</span>
                </p>

                <h3 className='mt-2 text-xl leading-snug font-semibold'>
                    <Link href={href} className='rounded-sm transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'>
                        <span data-mdlfy-cms={`courses:${course.id}:title`}>{course.title}</span>
                    </Link>
                </h3>

                <p className='mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`courses:${course.id}:summary`}>
                    {course.summary}
                </p>

                <div className='mt-4 flex flex-wrap items-center gap-2'>
                    <LevelBadge courseId={course.id} level={course.level} />

                    {course.duration && (
                        <span className='inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium text-muted-foreground'>
                            <Clock className='size-3.5' aria-hidden='true' />
                            <span data-mdlfy-cms={`courses:${course.id}:duration`}>{course.duration}</span>
                        </span>
                    )}
                </div>

                <div className='mt-auto pt-6'>
                    <div className='flex items-center justify-between gap-4 border-t pt-4'>
                        {course.price
                            ? (
                                <p className='font-heading text-xl font-semibold'>
                                    <span data-mdlfy-cms={`courses:${course.id}:price`}>{course.price}</span>
                                </p>
                            )
                            : <span />}

                        <Link
                            href={href}
                            aria-label={`View course: ${course.title}`}
                            className='inline-flex items-center gap-1.5 rounded-full text-sm font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                        >
                            View course
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    )
}
